import { useStore } from './store';
import { useChatStore } from './chatStore';
import { X, Plus, Trash2, Settings, Type, Shuffle, Image as ImageIcon, MousePointer2, ChevronDown, FileText, Zap, CheckCircle2, XCircle, Loader2, Link, Eye, EyeOff, Headphones } from 'lucide-react';
import { useState, useEffect } from 'react';
import CustomDropdown from '../CustomDropdown';
import api from '../../utils/api';

const RESPONSE_TYPES = [
  { id: 'text', label: 'Normal text', icon: Type },
  { id: 'random_text', label: 'Random text', icon: Shuffle },
  { id: 'image', label: 'Image', icon: ImageIcon },
  { id: 'button', label: 'Buttons', icon: MousePointer2 },
];

const STATUS_CONFIG = {
  idle:        { label: 'Not Connected',  color: 'text-zinc-500',    bg: 'bg-zinc-50',    border: 'border-zinc-200',   icon: Link },
  connected:   { label: 'Connected',      color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2 },
  failed:      { label: 'Connection Failed', color: 'text-rose-500', bg: 'bg-rose-50',    border: 'border-rose-200',   icon: XCircle },
  invalid_url: { label: 'Invalid URL',    color: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-200',  icon: XCircle },
  unauthorized:{ label: 'Unauthorized',   color: 'text-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-200',   icon: XCircle },
};

const INTEGRATION_LOGOS = {
  freshchat: '/freshchat.png',
  freshdesk: '/freshdesk.png',
  telegram: 'https://img.icons8.com/color/48/telegram-app.png',
  slack: '/slack.svg',
  whatsapp: 'https://img.icons8.com/color/48/whatsapp.png',
  zendesk: '/zendesk.png'
};

const renderIntegrationOption = (option) => {
  const logoUrl = INTEGRATION_LOGOS[option.id];
  return (
    <div className="flex items-center gap-2">
      {logoUrl && (
        <img 
          src={logoUrl} 
          alt={option.name} 
          className="w-4 h-4 object-contain rounded-md shrink-0" 
        />
      )}
      <span className="capitalize">{option.name}</span>
    </div>
  );
};

const WebhookPanel = ({ wh, setWh, nodeId, updateNodeData }) => {
  const [connecting, setConnecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [urlError, setUrlError] = useState('');

  const status = wh.connectionStatus || 'idle';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.idle;
  const StatusIcon = statusCfg.icon;

  const validateUrl = (url) => {
    if (!url.trim()) return 'URL is required';
    try { new URL(url); return ''; } catch { return 'Enter a valid URL (e.g. https://api.example.com)'; }
  };

  const handleConnect = async () => {
    const err = validateUrl(wh.url);
    if (err) { setUrlError(err); return; }
    setUrlError('');
    setConnecting(true);
    setWh({ connectionStatus: 'idle' });

    try {
      const headers = { 'Content-Type': 'application/json' };
      (wh.headers || []).forEach((h) => { if (h.key) headers[h.key] = h.value; });
      if (wh.basicAuthUsername) {
        headers['Authorization'] = 'Basic ' + btoa(`${wh.basicAuthUsername}:${wh.basicAuthPassword || ''}`);
      }
      if (wh.verificationToken) headers['X-Verification-Token'] = wh.verificationToken;

      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 8000);
      const res = await fetch(wh.url, { method: wh.method || 'POST', headers, signal: controller.signal });
      clearTimeout(tid);

      // Capture the full response body for the API Response viewer
      let responseBody = null;
      try {
        const ct = res.headers.get('content-type') || '';
        responseBody = ct.includes('application/json') ? await res.json() : await res.text();
      } catch {}

      const patch = {
        connectionStatus: res.status === 401 || res.status === 403
          ? 'unauthorized'
          : res.ok || res.status < 500 ? 'connected' : 'failed',
        lastTestResponse: responseBody,
        lastTestedAt: Date.now(),
      };
      setWh(patch);
    } catch (e) {
      setWh({ connectionStatus: 'failed' });
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="space-y-5 pt-4 border-t border-zinc-200/80">
      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${statusCfg.bg} ${statusCfg.border}`}>
        <StatusIcon size={16} className={statusCfg.color} />
        <span className={`text-xs font-black ${statusCfg.color}`}>{statusCfg.label}</span>
        {status === 'connected' && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
      </div>

      {/* Webhook Name */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">Webhook Name</label>
        <input
          type="text"
          value={wh.name || ''}
          onChange={(e) => setWh({ name: e.target.value })}
          placeholder="e.g. Order Confirmation"
          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
        />
      </div>

      {/* Method + URL */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">Webhook URL</label>
        <div className="flex gap-2">
          <CustomDropdown
            value={wh.method || 'POST'}
            onChange={(val) => setWh({ method: val, connectionStatus: 'idle' })}
            options={[
              { id: 'GET', name: 'GET' },
              { id: 'POST', name: 'POST' },
              { id: 'PUT', name: 'PUT' },
              { id: 'DELETE', name: 'DELETE' }
            ]}
            className="w-24 shrink-0"
            buttonClassName="py-2.5 text-[10px] font-black text-zinc-900"
          />
          <input
            type="text"
            value={wh.url || ''}
            onChange={(e) => { setWh({ url: e.target.value, connectionStatus: 'idle' }); setUrlError(''); }}
            placeholder="https://api.example.com/webhook"
            className={`flex-1 px-4 py-2.5 bg-zinc-50 border rounded-xl text-xs font-bold text-zinc-800 focus:outline-none focus:ring-2 focus:ring-indigo-100 outline-none transition-all ${
              urlError ? 'border-rose-300 bg-rose-50' : 'border-zinc-200/80'
            }`}
          />
        </div>
        {urlError && <p className="text-[10px] text-rose-500 font-bold pl-1">{urlError}</p>}
      </div>

      {/* Verification Token */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">Verification Token</label>
        <input
          type="text"
          value={wh.verificationToken || ''}
          onChange={(e) => setWh({ verificationToken: e.target.value })}
          placeholder="Optional token for request verification"
          className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
        />
      </div>

      {/* Basic Auth */}
      <div className="space-y-2.5 p-3.5 bg-zinc-50/70 rounded-xl border border-zinc-200/90">
        <span className="text-xs font-semibold text-zinc-800 tracking-tight block mb-2">Basic Authentication</span>
        <div className="space-y-2">
          <input
            type="text"
            value={wh.basicAuthUsername || ''}
            onChange={(e) => setWh({ basicAuthUsername: e.target.value })}
            placeholder="Username"
            className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800 outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={wh.basicAuthPassword || ''}
              onChange={(e) => setWh({ basicAuthPassword: e.target.value })}
              placeholder="Password"
              className="w-full px-3 py-2 pr-10 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800 outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
            >
              {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
      </div>

      {/* Dynamic Headers */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-zinc-700">Headers</span>
          <button
            onClick={() => setWh({ headers: [...(wh.headers || []), { key: '', value: '' }] })}
            className="flex items-center gap-1 text-[10px] font-black text-zinc-900 hover:text-black transition-colors"
          >
            <Plus size={12} /> Add Header
          </button>
        </div>
        {(wh.headers || []).length === 0 && (
          <p className="text-[10px] text-zinc-400 font-medium italic">No custom headers added</p>
        )}
        {(wh.headers || []).map((h, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              placeholder="Header name"
              value={h.key}
              onChange={(e) => setWh({ headers: wh.headers.map((x, j) => j === i ? { ...x, key: e.target.value } : x) })}
              className="w-2/5 px-3 py-2 bg-white border border-zinc-200 rounded-xl text-[10px] font-bold text-zinc-700 outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <input
              placeholder="Value"
              value={h.value}
              onChange={(e) => setWh({ headers: wh.headers.map((x, j) => j === i ? { ...x, value: e.target.value } : x) })}
              className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-xl text-[10px] font-bold text-zinc-700 outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <button onClick={() => setWh({ headers: wh.headers.filter((_, j) => j !== i) })} className="text-zinc-400 hover:text-rose-500 shrink-0">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>

      {/* Connect Button */}
      <button
        onClick={handleConnect}
        disabled={connecting}
        className={`w-full py-2.5 rounded-lg text-xs font-medium transition-all active:scale-[0.98] flex items-center justify-center gap-2 shadow-2xs ${
          connecting
            ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
            : status === 'connected'
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-zinc-900 text-white hover:bg-zinc-800'
        }`}
      >
        {connecting ? (
          <><Loader2 size={13} className="animate-spin" /> Testing Connection...</>
        ) : status === 'connected' ? (
          <><CheckCircle2 size={13} /> Reconnect Webhook</>
        ) : (
          <><Zap size={13} /> Connect Webhook</>
        )}
      </button>

      {status === 'failed' && (
        <p className="text-[10px] text-rose-500 font-bold text-center">Connection failed. Check the URL and try again.</p>
      )}
      {status === 'unauthorized' && (
        <p className="text-[10px] text-rose-500 font-bold text-center">Authentication failed. Check your credentials.</p>
      )}
      {status === 'invalid_url' && (
        <p className="text-[10px] text-rose-500 font-bold text-center">The URL format is invalid.</p>
      )}

      {/* ─── API Response Section ─── */}
      <WebhookResponseCard
        testResponse={wh.lastTestResponse}
        testResponseAt={wh.lastTestedAt}
        nodeId={nodeId}
      />
    </div>
  );
};

// ─── API Response JSON Viewer (black background card) ─────────────────────────

const JsonTreeNode = ({ data, path, depth, search }) => {
  const [expanded, setExpanded] = useState(depth < 2);

  if (data === null || data === undefined) {
    return <span className="text-gray-500 italic">{String(data)}</span>;
  }

  if (typeof data !== 'object') {
    const str = String(data);
    const isMatch = search && str.toLowerCase().includes(search.toLowerCase());
    if (typeof data === 'string') {
      return (
        <span className={`text-emerald-400 ${isMatch ? 'bg-amber-500/30 rounded px-0.5' : ''}`}>
          &quot;{str.length > 100 ? str.slice(0, 100) + '…' : str}&quot;
        </span>
      );
    }
    if (typeof data === 'boolean') {
      return <span className={`text-amber-400 ${isMatch ? 'bg-amber-500/30 rounded px-0.5' : ''}`}>{str}</span>;
    }
    return <span className={`text-sky-400 ${isMatch ? 'bg-amber-500/30 rounded px-0.5' : ''}`}>{str}</span>;
  }

  const isArray = Array.isArray(data);
  const entries = isArray
    ? data.map((v, i) => [String(i), v])
    : Object.entries(data);
  const isEmpty = entries.length === 0;

  const fullPath = (key) => path ? `${path}.${key}` : key;

  const filteredEntries = search
    ? entries.filter(([k, v]) => {
        const s = search.toLowerCase();
        return k.toLowerCase().includes(s) || String(v).toLowerCase().includes(s);
      })
    : entries;

  if (isEmpty) {
    return <span className="text-gray-500">{isArray ? '[]' : '{}'}</span>;
  }

  return (
    <div>
      <button
        onClick={() => setExpanded(p => !p)}
        className="inline-flex items-center gap-1 hover:text-indigo-400 transition-colors group/expand"
      >
        <ChevronDown size={10} className={`text-gray-500 transition-transform ${expanded ? '' : '-rotate-90'}`} />
        <span className="text-indigo-400 font-bold">
          {isArray ? `Array[${entries.length}]` : `Object{${entries.length}}`}
        </span>
      </button>
      {expanded && (
        <div className="ml-3 border-l border-gray-700 pl-2 space-y-0.5">
          {filteredEntries.map(([k, v]) => (
            <div key={k} className="flex items-start gap-1 group/field">
              <span className="text-violet-400 font-bold shrink-0 text-[11px]">
                {isArray ? <span className="text-sky-400">[{k}]</span> : k}:
              </span>
              <div className="flex-1 min-w-0">
                <JsonTreeNode data={v} path={fullPath(k)} depth={depth + 1} search={search} />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard?.writeText(`{{webhook.${fullPath(k)}}}`);
                }}
                className="opacity-0 group-hover/field:opacity-100 text-gray-600 hover:text-indigo-400 transition-opacity shrink-0 mt-0.5 px-1"
                title="Copy variable path"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                </svg>
              </button>
            </div>
          ))}
          {filteredEntries.length < entries.length && (
            <p className="text-gray-600 italic text-[10px]">{entries.length - filteredEntries.length} hidden by search</p>
          )}
        </div>
      )}
    </div>
  );
};

const WebhookResponseCard = ({ testResponse, testResponseAt, nodeId }) => {
  const search = '';
  const [collapsed, setCollapsed] = useState(false);

  // Read live webhookResponse from chat store (updated after every chat-driven execution)
  const liveWebhookResponse = useChatStore((s) => s.variables?.webhookResponse);
  const liveHistory = useChatStore((s) => s.history);

  // Find the most recent api_response for this specific node from chat history
  const lastNodeApiResponse = liveHistory
    .filter((item) => item.nodeId === nodeId && item.type === 'api_response')
    .pop();

  // Priority: live store variable > node-specific chat response > manual test response
  const response = liveWebhookResponse || lastNodeApiResponse?.apiData || testResponse;
  const source = liveWebhookResponse ? 'chat' : lastNodeApiResponse?.apiData ? 'chat' : 'test';
  const testedAt = testResponseAt;

  // No response yet
  if (!response) {
    return (
      <div className="rounded-xl bg-gray-950 border border-gray-800 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">API Response</span>
        </div>
        <div className="px-4 py-6 text-center">
          <p className="text-[10px] text-gray-600 italic">No response yet. Run the chat flow or click "Connect Webhook".</p>
        </div>
      </div>
    );
  }

  // Parse response if it's a string
  let parsed = response;
  if (typeof response === 'string') {
    try { parsed = JSON.parse(response); } catch { parsed = { value: response }; }
  }

  const timeAgo = testedAt
    ? new Date(testedAt).toLocaleTimeString()
    : '';

  return (
    <div className="rounded-xl bg-gray-950 border border-gray-800 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${source === 'chat' ? 'bg-emerald-500 animate-pulse' : 'bg-sky-500'}`} />
          <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">API Response</span>
          {source === 'chat' && (
            <span className="text-[8px] font-bold text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-md uppercase">Live</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {timeAgo && <span className="text-[9px] text-gray-600">{timeAgo}</span>}
          <button
            onClick={() => setCollapsed(p => !p)}
            className="text-[9px] font-bold text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            {collapsed ? 'Expand' : 'Collapse'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          

         {/* JSON Tree */}
          <div className="px-4 py-3 max-h-[320px] overflow-y-auto custom-scrollbar">
            <JsonTreeNode data={parsed} path="" depth={0} search={search} />
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2 border-t border-gray-800 flex items-center justify-between">
            <button
              onClick={() => navigator.clipboard?.writeText(JSON.stringify(parsed, null, 2))}
              className="text-[8px] font-bold text-gray-500 hover:text-indigo-400 transition-colors"
            >
              Copy Raw JSON
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── AI Agent Panel ────────────────────────────────────────────────────────────
// Lightweight UI — only prompt override, input, and variable mapping.
// All provider/model/API key config is managed centrally on the server.

const AiAgentPanel = ({ config, setConfig }) => {
  return (
    <div className="space-y-5 pt-4 border-t border-zinc-200/80">
      {/* System Prompt */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">
          System Prompt
        </label>
        <textarea
          value={config.systemPromptOverride || ''}
          onChange={(e) => setConfig({ systemPromptOverride: e.target.value })}
          placeholder="Enter your bot behaviour here"
          className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors min-h-[72px] resize-none"/>
      </div>

      {/* User Prompt / Input */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">User Prompt / Input</label>
        <textarea
          value={config.userInput || ''}
          onChange={(e) => setConfig({ userInput: e.target.value })}
          placeholder="Use variables like {{lastUserInput}} or {{user_name}}"
          className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors min-h-[72px] resize-none"
        />
        <p className="text-[9px] text-zinc-500 italic pl-1">
          Supports variable templates: {'{{lastUserInput}}'}, {'{{user_name}}'}, etc.
        </p>
      </div>

      {/* Output Variable */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-700 block">Output Variable</label>
        <input
          type="text"
          value={config.outputVariable || 'aiResponse'}
          onChange={(e) => setConfig({ outputVariable: e.target.value })}
          placeholder="e.g. aiResponse"
          className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200/80 rounded-xl text-xs font-bold text-zinc-900 focus:outline-none focus:ring-4 focus:ring-zinc-100 focus:border-zinc-900 transition-all"
        />
        <p className="text-[9px] text-zinc-500 italic pl-1">
          Access the AI response in downstream nodes using {'{{' + (config.outputVariable || 'aiResponse') + '}}'}
        </p>
      </div>

      {/* Model Hyperparameters (Aesthetic Collapse/Layout) */}
      <div className="space-y-4 pt-4 border-t border-zinc-200/80">
        <span className="text-xs font-semibold text-zinc-800 tracking-tight block">Intelligence Tuning</span>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-zinc-600 block">Temperature</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={config.temperature !== undefined ? config.temperature : ''}
              onChange={(e) => {
                const val = e.target.value;
                setConfig({ temperature: val === '' ? undefined : parseFloat(val) });
              }}
              placeholder="e.g. 0.7 (Default)"
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-zinc-600 block">Top P</label>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={config.topP !== undefined ? config.topP : ''}
              onChange={(e) => {
                const val = e.target.value;
                setConfig({ topP: val === '' ? undefined : parseFloat(val) });
              }}
              placeholder="e.g. 0.9 (Default)"
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-[11px] font-medium text-zinc-600 block">Max Output Tokens</label>
          <input
            type="number"
            min="1"
            value={config.maxTokens !== undefined ? config.maxTokens : ''}
            onChange={(e) => {
              const val = e.target.value;
              setConfig({ maxTokens: val === '' ? undefined : parseInt(val) });
            }}
            placeholder="e.g. 2048 (Default)"
            className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
          />
        </div>
      </div>
    </div>
  );
};

export const PropertyPanel = ({ onClose }) => {
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const nodes = useStore((s) => s.nodes);
  const updateNodeData = useStore((s) => s.updateNodeData);
  const setSelectedNodeId = useStore((s) => s.setSelectedNodeId);

  const queryParams = new URLSearchParams(window.location.search);
  const currentFlowId = queryParams.get('id');

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  const data = selectedNode?.data;

  const [flows, setFlows] = useState([]);
  useEffect(() => {
    if (data?.type === 'goto') {
      const fetchFlows = async () => {
        try {
          const res = await api.getBotFlows();
          setFlows(res || []);
        } catch (e) {
          console.error('Failed to fetch flows for goto node:', e);
        }
      };
      fetchFlows();
    }
  }, [data?.type]);

  const [integrationStatuses, setIntegrationStatuses] = useState({
    freshchat: { active: false, label: "Freshchat" },
    freshdesk: { active: false, label: "Freshdesk" },
    telegram: { active: false, label: "Telegram" },
    slack: { active: false, label: "Slack" },
    whatsapp: { active: false, label: "WhatsApp" },
    zendesk: { active: false, label: "Zendesk" },
  });
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  useEffect(() => {
    if (data?.type === 'integration') {
      const fetchStatuses = async () => {
        setLoadingIntegrations(true);
        const aiAgentId = localStorage.getItem('activeAiAgentId');
        try {
          const fc = await api.request("/freshchat/config", { aiAgentId }).catch(() => null);
          const fd = await api.request("/freshdesk/config", { aiAgentId }).catch(() => null);
          const tg = await api.request("/telegram/config", { aiAgentId }).catch(() => null);
          const sl = await api.request("/slack/config", { aiAgentId }).catch(() => null);
          const wa = await api.request("/whatsapp/config", { aiAgentId }).catch(() => null);
          const zd = await api.request("/zendesk/config", { aiAgentId }).catch(() => null);

          setIntegrationStatuses({
            freshchat: { active: !!fc?.config?.is_active, label: "Freshchat" },
            freshdesk: { active: !!fd?.config?.is_active, label: "Freshdesk" },
            telegram: { active: !!tg?.config?.is_active, label: "Telegram" },
            slack: { active: !!sl?.config?.is_active, label: "Slack" },
            whatsapp: { active: !!wa?.config?.is_active, label: "WhatsApp" },
            zendesk: { active: !!zd?.config?.is_active, label: "Zendesk" },
          });
        } catch (e) {
          console.error("Failed to load integrations status:", e);
        } finally {
          setLoadingIntegrations(false);
        }
      };
      fetchStatuses();
    }
  }, [data?.type]);

  const [codeSyntaxError, setCodeSyntaxError] = useState('');
  useEffect(() => {
    if (data?.type === 'function') {
      const code = data.functionConfig?.code || '';
      if (!code.trim()) {
        setCodeSyntaxError('');
        return;
      }
      try {
        // eslint-disable-next-line no-new-func
        new Function('input', code);
        setCodeSyntaxError('');
      } catch (err) {
        setCodeSyntaxError(err.message);
      }
    } else {
      setCodeSyntaxError('');
    }
  }, [data?.functionConfig?.code, data?.type]);

  if (!selectedNode) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-zinc-50/50">
        <div className="w-11 h-11 bg-white rounded-xl shadow-2xs border border-zinc-200 flex items-center justify-center text-zinc-400 mb-3">
          <Settings size={20} />
        </div>
        <p className="text-xs font-medium text-zinc-500">Select a node on the canvas to configure properties</p>
      </div>
    );
  }

  const handleChange = (field, value) => {
    updateNodeData(selectedNode.id, { [field]: value });
  };

  const dynamicVariables = nodes
  .filter(n => (n.data.type === 'input' && n.data.variableName) || n.data.type === 'function')
  .flatMap(n => {
    if (n.data.type === 'input' && n.data.variableName) {
      return n.data.variableName;
    }
    if (n.data.type === 'function') {
      // Return output variables that function nodes can set
      return ['isAdult', 'yearsUntilAdult', 'age', 'email', 'output'];
    }
    return [];
  });

  const imageVariables = nodes
    .filter(n => n.data.type === 'input' && n.data.variableName && (n.data.inputMode === 'image' || n.data.inputMode === 'text+image'))
    .flatMap(n => [
      { label: `${n.data.variableName}.url`, value: `${n.data.variableName}.url` },
      { label: `${n.data.variableName}.filename`, value: `${n.data.variableName}.filename` },
      { label: `${n.data.variableName}.mimeType`, value: `${n.data.variableName}.mimeType` },
      { label: `${n.data.variableName}.error`, value: `${n.data.variableName}.error` },
    ]);

  const nodeOptions = [...new Map(
  nodes
    .filter(n => n.id !== selectedNode.id)
    .map(n => ({
      id: n.id,
      label: n.data.customId ? `[${n.data.customId}] ${n.data.label}` : n.data.label,
      customId: n.data.customId,
    }))
    .map(opt => [opt.id, opt])
).values()];

  const conditionFields = [
    {
      group: "System",
      options: [
        { label: "Last User Input", value: "lastUserInput" },
        { label: "Last Clicked Button", value: "lastClickedButton" },
        { label: "Last Uploaded Image", value: "lastUploadedImage" }
      ]
    },
    {
      group: "Webhook Response",
      options: [
        { label: "Full Response (JSON)", value: "webhookResponse" },
        { label: "Response → data", value: "webhookResponse.data" },
        { label: "Response → status", value: "webhookResponse.status" },
        { label: "Response → message", value: "webhookResponse.message" },
        { label: "Response → success", value: "webhookResponse.success" },
        { label: "Response → error", value: "webhookResponse.error" },
      ]
    },
    {
      group: "User Attributes",
      options: [
        { label: "User Name", value: "user.name" },
        { label: "User Email", value: "user.email" },
        { label: "User Phone", value: "user.phone" },
        { label: "User Location", value: "user.location" }
      ]
    },
    {
      group: "Custom Variables",
      options: dynamicVariables.map(v => ({ label: v, value: v }))
    },
    ...(imageVariables.length > 0 ? [{ group: "Image Upload Variables", options: imageVariables }] : [])
  ];

  const addBranch = () => {
    const branches = [...(data.branches || [])];
    branches.push({ 
      id: Math.random().toString(), 
      rule: { field: 'lastUserInput', operator: 'equals', value: '' }, 
      targetNodeId: '' 
    });
    handleChange('branches', branches);
  };

  const updateBranch = (id, updates) => {
    const branches = data.branches?.map((b) =>
      b.id === id ? { ...b, ...updates } : b
    );
    handleChange('branches', branches);
  };

  const updateBranchRule = (id, updates) => {
    const branches = data.branches?.map((b) =>
      b.id === id ? { ...b, rule: { ...b.rule, ...updates } } : b
    );
    handleChange('branches', branches);
  };

  const removeBranch = (id) => {
    const branches = data.branches?.filter((b) => b.id !== id);
    handleChange('branches', branches);
  };

  const updateButton = (messageId, buttonId, updates) => {
    const messages = data.messages?.map((m) => {
      if (m.id === messageId) {
        const buttons = m.buttons?.map(b => b.id === buttonId ? { ...b, ...updates } : b);
        return { ...m, buttons };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const removeMessage = (id) => {
    const messages = data.messages?.filter((m) => m.id !== id);
    handleChange('messages', messages);
  };

  const addMessageOfType = (type) => {
    const messages = [...(data.messages || [])];
    const newMessage = { 
      id: Math.random().toString(), 
      type,
      content: '',
      items: type === 'random_text' || type === 'image' ? [''] : undefined,
      buttons: type === 'button' ? [{ id: Math.random().toString(), label: 'Button 1' }] : undefined
    };
    messages.push(newMessage);
    handleChange('messages', messages);
  };

  const updateMessageField = (id, field, value) => {
    const messages = data.messages?.map((m) =>
      m.id === id ? { ...m, [field]: value } : m
    );
    handleChange('messages', messages);
  };

  const updateMessageItem = (id, index, value) => {
    const messages = data.messages?.map((m) => {
      if (m.id === id && m.items) {
        const newItems = [...m.items];
        newItems[index] = value;
        return { ...m, items: newItems };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const addMessageItem = (id) => {
    const messages = data.messages?.map((m) => {
      if (m.id === id) {
        return { ...m, items: [...(m.items || []), ''] };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const removeMessageItem = (id, index) => {
    const messages = data.messages?.map((m) => {
      if (m.id === id && m.items) {
        return { ...m, items: m.items.filter((_, i) => i !== index) };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const addButton = (messageId) => {
    const messages = data.messages?.map((m) => {
      if (m.id === messageId) {
        const buttons = m.buttons || [];
        return { 
          ...m, 
          buttons: [...buttons, { id: Math.random().toString(), label: 'New Button' }] 
        };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const removeButton = (messageId, buttonId) => {
    const messages = data.messages?.map((m) => {
      if (m.id === messageId) {
        const buttons = m.buttons?.filter(b => b.id !== buttonId);
        return { ...m, buttons };
      }
      return m;
    });
    handleChange('messages', messages);
  };

  const addFormField = () => {
    const formFields = [...(data.formFields || [])];
    formFields.push({
      id: Math.random().toString(36).substr(2, 9),
      label: 'New Field',
      variableName: 'field_' + (formFields.length + 1),
      type: 'text',
      required: true
    });
    handleChange('formFields', formFields);
  };

  const updateFormField = (id, updates) => {
    const formFields = data.formFields?.map(f => f.id === id ? { ...f, ...updates } : f);
    handleChange('formFields', formFields);
  };

  const removeFormField = (id) => {
    const formFields = data.formFields?.filter(f => f.id !== id);
    handleChange('formFields', formFields);
  };

  return (
    <div className="w-full h-full flex flex-col bg-white overflow-hidden">
      <div className="p-3.5 border-b border-zinc-200 flex items-center justify-between">
         <h3 className="text-xs font-semibold text-zinc-900 tracking-tight">Node Properties</h3>
         <div className="flex items-center gap-1">
           <button 
             onClick={() => {
               setSelectedNodeId(null);
               if (onClose) onClose();
             }}
             className="p-1.5 hover:bg-zinc-100 rounded-lg text-zinc-400 hover:text-zinc-700 transition-colors"
             title="Close panel"
           >
             <X size={15} />
           </button>
         </div>
       </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">
        {/* Basic Config */}
        <section className="space-y-3">
          <div>
            <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
              Node Label
            </label>
            <input
              type="text"
              value={data.label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
              Node ID
            </label>
            <div className="px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-500 cursor-not-allowed select-all">
              {data.customId || '-'}
            </div>
          </div>
          
          {data.type === 'goto' && (
            <div className="space-y-4 pt-4 border-t border-zinc-200/80">
               <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                    Jump to Node
                  </label>
                  <CustomDropdown
                    value={data.isFlowJump ? '__flow_jump__' : (data.targetNodeId || '')}
                    onChange={(val) => {
                      if (val === '__flow_jump__') {
                        updateNodeData(selectedNode.id, {
                          isFlowJump: true,
                          targetNodeId: '',
                          targetFlowId: data.targetFlowId || '',
                          targetFlowName: data.targetFlowName || ''
                        });
                      } else {
                        updateNodeData(selectedNode.id, {
                          isFlowJump: false,
                          targetNodeId: val,
                          targetFlowId: '',
                          targetFlowName: ''
                        });
                      }
                    }}
                    options={[
                      { id: '', name: 'Select target node...' },
                      ...nodeOptions.map(opt => ({
                        id: opt.id,
                        name: opt.label
                      })),
                      { id: '__flow_jump__', name: 'Jump to flow' }
                    ]}
                  />
                  <p className="mt-2 text-[10px] font-medium text-zinc-500 leading-relaxed">
                    This node will immediately redirect the flow to the selected node or flow.
                  </p>
               </div>

               {data.isFlowJump && (
                 <div className="space-y-2">
                    <label className="text-xs font-medium text-zinc-700 block">
                      Target Flow
                    </label>
                    <CustomDropdown
                      value={data.targetFlowId || ''}
                      onChange={(val) => {
                        const selectedFlow = flows.find(f => String(f.id) === String(val));
                        updateNodeData(selectedNode.id, {
                          targetFlowId: val,
                          targetFlowName: selectedFlow ? selectedFlow.name : ''
                        });
                      }}
                      options={[
                        { id: '', name: 'Select flow...' },
                        ...flows
                          .filter(f => String(f.id) !== String(currentFlowId))
                          .map(f => ({
                            id: String(f.id),
                            name: f.name
                          }))
                      ]}
                    />
                 </div>
               )}
            </div>
          )}

          {data.type === 'action' && (() => {
            const DEFAULT_WEBHOOK = { name: '', url: '', method: 'POST', verificationToken: '', basicAuthUsername: '', basicAuthPassword: '', headers: [], params: [], body: '', timeout: 10000, retries: 0, mappings: [], connectionStatus: 'idle' };
            const wh = { ...DEFAULT_WEBHOOK, ...(data.webhook || {}) };
            const setWh = (patch) => handleChange('webhook', { ...wh, ...patch });
            return <WebhookPanel wh={wh} setWh={setWh} nodeId={selectedNode.id} updateNodeData={updateNodeData} />;
          })()}

          {data.type === 'ai_agent' && (() => {
            const DEFAULT_AI = { systemPromptOverride: '', userInput: '{{lastUserInput}}', outputVariable: 'aiResponse' };
            const aiCfg = { ...DEFAULT_AI, ...(data.aiConfig || {}) };
            const setAiCfg = (patch) => handleChange('aiConfig', { ...aiCfg, ...patch });
            return <AiAgentPanel config={aiCfg} setConfig={setAiCfg} />;
          })()}

{data.type === 'function' && (
  <div className="space-y-4 pt-4 border-t border-zinc-200/80">
    <div>
      <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
        JavaScript Code
      </label>
      <textarea
        value={data.functionConfig?.code || ''}
        onChange={(e) => {
          const val = e.target.value;
          // Validate syntax error immediately as the user types
          if (!val.trim()) {
            setCodeSyntaxError('');
          } else {
            try {
              // eslint-disable-next-line no-new-func
              new Function('input', val);
              setCodeSyntaxError('');
            } catch (err) {
              setCodeSyntaxError(err.message);
            }
          }
          const cfg = { ...(data.functionConfig || {}), code: val };
          handleChange('functionConfig', cfg);
        }}
        placeholder="// Your code here"
        className="w-full px-3 py-2 bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-lg text-xs font-mono focus:outline-none focus:border-zinc-700 transition-colors min-h-[120px] resize-none"
      />
      {codeSyntaxError ? (
        <div className="flex items-start gap-2 mt-2 px-3 py-2 bg-rose-50 border border-rose-100 rounded-xl">
          <XCircle size={14} className="text-rose-500 shrink-0 mt-0.5" />
          <p className="text-[10px] text-rose-600 font-bold leading-snug">{codeSyntaxError}</p>
        </div>
      ) : data.functionConfig?.code ? (
        <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
          <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
          <p className="text-[10px] text-emerald-600 font-bold">No syntax errors detected</p>
        </div>
      ) : null}
      <p className="mt-2 text-[10px] text-zinc-500 italic">
        Input available as <code>input</code> variable. Access variables via <code>input.variableName</code>.
      </p>
    </div>
  </div>
)}          

{data.type === 'escalate' && (
  <div className="space-y-4 pt-4 border-t border-zinc-200/80">
    <div className="flex items-start gap-3 p-4 bg-orange-50/50 border border-orange-100 rounded-xl">
      <Headphones size={18} className="text-orange-600 shrink-0 mt-0.5" />
      <div>
        <h4 className="text-xs font-black text-orange-800 uppercase tracking-wider mb-1">Human Handoff</h4>
        <p className="text-[10px] text-orange-600 font-bold leading-relaxed">
          This node automatically escalates to a human agent.
        </p>
      </div>
    </div>
  </div>
)}

          {data.type === 'input' && (
            <div className="space-y-6">
               <div className="flex bg-zinc-100 p-0.5 rounded-lg border border-zinc-200/60">
                  <button 
                    onClick={() => handleChange('inputType', 'text')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${data.inputType !== 'form' ? 'bg-white text-zinc-900 shadow-2xs font-semibold' : 'text-zinc-500 hover:text-zinc-800'}`}
                  >
                    Text Input
                  </button>
                  <button 
                    onClick={() => handleChange('inputType', 'form')}
                    className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${data.inputType === 'form' ? 'bg-white text-zinc-900 shadow-2xs font-semibold' : 'text-zinc-500 hover:text-zinc-800'}`}
                  >
                    Form Mode
                  </button>
               </div>

               {data.inputType !== 'form' && (
                 <div className="space-y-2">
                   <label className="text-xs font-medium text-zinc-700 block">Input Mode</label>
                   <div className="flex bg-zinc-100 p-1 rounded-xl">
                     {(['text', 'image', 'text+image']).map(mode => (
                       <button
                         key={mode}
                         onClick={() => handleChange('inputMode', mode)}
                         className={`flex-1 py-2 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all ${
                           (data.inputMode || 'text') === mode ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                         }`}
                       >
                         {mode}
                       </button>
                     ))}
                   </div>
                 </div>
               )}

               {data.inputType === 'form' ? (
                 <div className="space-y-4">
                    <div className="flex items-center justify-between">
                       <label className="text-xs font-medium text-zinc-700 block">Form Fields</label>
                       <button
                         onClick={addFormField}
                         className="p-1 bg-zinc-100 text-zinc-900 rounded-lg hover:bg-indigo-100 transition-all"
                       >
                         <Plus size={16} />
                       </button>
                    </div>
                    <div className="space-y-4">
                       {data.formFields?.map((field, idx) => (
                         <div key={field.id} className="p-4 bg-zinc-50 rounded-xl border border-zinc-200/80 space-y-3">
                            <div className="flex justify-between items-center">
                               <span className="text-[9px] font-bold text-zinc-500 uppercase">Field #{idx + 1}</span>
                               <button onClick={() => removeFormField(field.id)} className="text-zinc-400 hover:text-rose-500">
                                 <Trash2 size={12} />
                               </button>
                            </div>
                            <div className="space-y-2">
                               <input
                                 type="text"
                                 value={field.label}
                                 onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                                 placeholder="Field Label"
                                 className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800 outline-none"
                               />
                               <div className="grid grid-cols-2 gap-2">
                                  <CustomDropdown
                                    value={field.type}
                                    onChange={(val) => updateFormField(field.id, { type: val })}
                                    options={[
                                      { id: 'text', name: 'Text' },
                                      { id: 'number', name: 'Number' },
                                      { id: 'email', name: 'Email' },
                                      { id: 'tel', name: 'Phone' }
                                    ]}
                                    className="w-full"
                                    buttonClassName="py-2 text-[10px] font-bold"
                                  />
                                  <input
                                    type="text"
                                    value={field.variableName}
                                    onChange={(e) => updateFormField(field.id, { variableName: e.target.value })}
                                    placeholder="Var Name"
                                    className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-[10px] font-bold text-zinc-900 outline-none"
                                  />
                               </div>
                            </div>
                         </div>
                       ))}
                       {(!data.formFields || data.formFields.length === 0) && (
                         <div className="text-center py-8 border-2 border-dashed border-zinc-200/80 rounded-xl">
                            <FileText size={24} className="text-slate-200 mx-auto mb-2" />
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">No fields added yet</p>
                         </div>
                       )}
                    </div>
                 </div>
               ) : (
                 <div className="space-y-4">
                    <div>
                       <label className="text-xs font-medium text-zinc-700 mb-1.5 block">Question / Prompt</label>
                       <textarea
                         value={data.inputPrompt || ''}
                         onChange={(e) => handleChange('inputPrompt', e.target.value)}
                         placeholder="Enter the question for the user..."
                         className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors min-h-[72px] resize-none"
                       />
                    </div>
                    <div>
                       <label className="text-xs font-medium text-zinc-700 mb-1.5 block">Save to Attribute</label>
                       <input
                         type="text"
                         value={data.variableName || ''}
                         onChange={(e) => handleChange('variableName', e.target.value)}
                         placeholder="e.g. user_name"
                         className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
                       />
                    </div>
                 </div>
               )}
            </div>
          )}

          {data.type === 'input' && (
            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                Keyword Matcher (Optional)
              </label>
              <input
                type="text"
                value={data.keywords || ''}
                onChange={(e) => handleChange('keywords', e.target.value)}
                placeholder="e.g. yes, sure, okay (comma separated)"
                className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
              />
              <p className="text-[10px] text-zinc-500 mt-2 italic">
                If set, the flow only continues if the input matches one of these.
              </p>
            </div>
          )}
        </section>

        {/* Type Specific Config */}
        {(data.type === 'message' || data.type === 'input') && (
          <section className="space-y-6">
            <div>
              <label className="text-xs font-medium text-zinc-700 block mb-2.5">
                Responses
              </label>
              <div className="grid grid-cols-3 gap-2">
                {RESPONSE_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => addMessageOfType(type.id)}
                    className="flex flex-col items-center justify-center gap-1.5 p-2.5 bg-white border border-zinc-200/90 rounded-lg hover:border-zinc-300 hover:bg-zinc-50 shadow-2xs transition-all group"
                  >
                    <type.icon size={16} className="text-zinc-500 group-hover:text-zinc-900 transition-colors" />
                    <span className="text-[11px] font-medium text-zinc-700 group-hover:text-zinc-900">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              {data.messages?.map((msg, msgIdx) => (
                <div key={msg.id} className="space-y-3 p-3.5 bg-zinc-50/70 rounded-xl border border-zinc-200/90 relative group/msg">
                  <header className="flex justify-between items-center bg-zinc-100/60 -m-3.5 mb-2.5 p-2.5 rounded-t-xl border-b border-zinc-200/80">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const typeInfo = RESPONSE_TYPES.find(t => t.id === msg.type);
                        const Icon = typeInfo?.icon || Type;
                        return (
                          <>
                            <Icon size={12} className="text-zinc-500" />
                            <span className="text-[10px] font-semibold text-zinc-700 uppercase tracking-wider">
                              {typeInfo?.label || 'Response'} #{msgIdx + 1}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                    <button 
                      onClick={() => removeMessage(msg.id)}
                      className="p-1 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </header>
                  
                  {/* Type-Specific Fields */}
                  {(msg.type === 'text' || msg.type === 'button' || msg.type === 'image') && (
                    <div className="space-y-1">
                      <label className="text-[10px] font-medium text-zinc-500 pl-0.5">Message Text</label>
                      <textarea
                        value={msg.content || ''}
                        onChange={(e) => updateMessageField(msg.id, 'content', e.target.value)}
                        placeholder="Enter bot message..."
                        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 min-h-[60px] resize-none transition-all"
                      />
                    </div>
                  )}

                  {msg.type === 'image' && (
                    <div className="space-y-2">
                      <div className="space-y-1">
                        <label className="text-[10px] font-medium text-zinc-500 pl-0.5">Image URL</label>
                        <input
                          type="url"
                          value={msg.items?.[0] || ''}
                          onChange={(e) => updateMessageField(msg.id, 'items', [e.target.value])}
                          placeholder="https://... (JPG, PNG, GIF)"
                          className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 transition-all"
                        />
                      </div>
                      
                      {msg.items?.[0] && (
                        <div className="relative group/img-prev rounded-xl overflow-hidden border border-zinc-200 aspect-video bg-zinc-100">
                          <img 
                            src={msg.items[0]} 
                            alt="Preview" 
                            className="w-full h-full object-cover" 
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              (e.target).src = 'https://via.placeholder.com/400x225?text=Invalid+Image+URL';
                            }}
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img-prev:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-[10px] font-bold text-white uppercase tracking-widest">Image Preview</span>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-[9px] text-zinc-500 italic px-1">
                        Supports standard image formats like JPEG, PNG, or GIF. You can also use variable templates like {"{{image_url}}"}.
                      </p>
                    </div>
                  )}

                  {msg.type === 'random_text' && (
                    <div className="space-y-2">
                       <label className="text-[10px] font-medium text-zinc-500 pl-0.5 block">Variations (Bot will pick one)</label>
                       {msg.items?.map((item, i) => (
                         <div key={i} className="flex gap-2">
                            <input
                              value={item}
                              onChange={(e) => updateMessageItem(msg.id, i, e.target.value)}
                              placeholder={`Variant ${i+1}`}
                              className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-700 outline-none focus:ring-2 focus:ring-indigo-100"
                            />
                            <button onClick={() => removeMessageItem(msg.id, i)} className="text-zinc-400 hover:text-rose-500"><Trash2 size={12}/></button>
                         </div>
                       ))}
                       <button onClick={() => addMessageItem(msg.id)} className="w-full py-2 border-2 border-dashed border-zinc-200 rounded-xl text-[10px] font-bold text-zinc-500 hover:border-indigo-200 hover:text-indigo-400 transition-all flex items-center justify-center gap-1">
                         <Plus size={12} /> Add Variation
                       </button>
                    </div>
                  )}

                  {msg.type === 'button' && (
                    <div className="pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">Buttons</span>
                        <button 
                          onClick={() => addButton(msg.id)}
                          className="text-[10px] font-bold text-zinc-900 hover:text-black flex items-center gap-1"
                        >
                          <Plus size={12} /> Add Button
                        </button>
                      </div>
                      <div className="space-y-2">
                        {msg.buttons?.map(btn => (
                          <div key={btn.id} className="space-y-2 p-2 bg-white border border-zinc-200 rounded-xl group/btn">
                            <div className="flex gap-2 items-center">
                              <input 
                                type="text"
                                value={btn.label}
                                onChange={(e) => updateButton(msg.id, btn.id, { label: e.target.value })}
                                placeholder="Label"
                                className="flex-1 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-[10px] font-bold text-zinc-700 focus:ring-2 focus:ring-indigo-100 outline-none"
                              />
                              <button 
                                onClick={() => removeButton(msg.id, btn.id)}
                                className="text-zinc-400 hover:text-rose-500 transition-opacity"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                            
                            <CustomDropdown
                              value={btn.targetNodeId || ''}
                              onChange={(val) => {
                                const updates = { targetNodeId: val };
                                if (val !== 'add_url') {
                                  updates.url = '';
                                }
                                updateButton(msg.id, btn.id, updates);
                              }}
                              options={[
                                { id: '', name: 'Default Routing (Edge)' },
                                { id: 'add_url', name: 'Add URL' },
                                ...nodeOptions.map(opt => ({
                                  id: opt.id,
                                  name: `Go to: ${opt.label}`
                                }))
                              ]}
                              className="w-full"
                              buttonClassName="py-2 text-[10px] font-bold"
                            />

                            {btn.targetNodeId === 'add_url' && (
                              <div className="space-y-1 mt-1">
                                <input 
                                  type="text"
                                  value={btn.url || ''}
                                  onChange={(e) => updateButton(msg.id, btn.id, { url: e.target.value })}
                                  placeholder="Enter URL (e.g. https://example.com)"
                                  className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-[10px] font-bold text-zinc-700 focus:ring-2 focus:ring-indigo-100 outline-none"
                                />
                                <p className="text-[9px] text-zinc-500 italic">Clicking this button will open this URL in a new tab.</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {data.type === 'condition' && (
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-700 block">
                Conditions
              </label>
              <button 
                onClick={addBranch}
                className="text-xs font-bold text-zinc-900 hover:text-black flex items-center gap-1"
              >
                <Plus size={14} /> Add Branch
              </button>
            </div>

            <p className="text-[10px] text-zinc-500 italic">Branches are evaluated top-to-bottom. First match wins.</p>
            
            <div className="space-y-4">
              {data.branches?.map((branch, idx) => (
                <div key={branch.id} className="p-3.5 bg-zinc-50/70 rounded-xl border border-zinc-200/90 space-y-2.5 relative group">
                  <header className="flex justify-between items-center gap-2 pb-1 border-b border-zinc-200/60">
                    <input 
                      type="text"
                      value={branch.label || ''}
                      onChange={(e) => updateBranch(branch.id, { label: e.target.value })}
                      placeholder={`Branch #${idx + 1}`}
                      className="text-xs font-semibold text-zinc-800 bg-transparent border-none outline-none focus:text-zinc-900 w-full"
                    />
                    <button 
                      onClick={() => removeBranch(branch.id)}
                      className="p-1 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors shrink-0"
                    >
                      <Trash2 size={12} />
                    </button>
                  </header>

                  <div className="grid grid-cols-2 gap-2">
                    <CustomDropdown
                      value={branch.rule.field}
                      onChange={(val) => updateBranchRule(branch.id, { field: val })}
                      options={conditionFields.flatMap(group => 
                        group.options.map(opt => ({
                          id: opt.value,
                          name: `${group.group} → ${opt.label}`
                        }))
                      )}
                      className="w-full"
                      buttonClassName="py-2 text-[10px] font-bold"
                    />

                    <CustomDropdown
                      value={branch.rule.operator}
                      onChange={(val) => updateBranchRule(branch.id, { operator: val })}
                      options={[
                        { id: 'equals', name: 'Equals' },
                        { id: 'not_equals', name: 'Does not equal' },
                        { id: 'contains', name: 'Contains' },
                        { id: 'starts_with', name: 'Starts with' },
                        { id: 'ends_with', name: 'Ends with' },
                        { id: 'greater_than', name: 'Greater than' },
                        { id: 'less_than', name: 'Less than' },
                        { id: 'is_empty', name: 'Is empty' },
                        { id: 'is_not_empty', name: 'Is not empty' },
                        { id: 'image_exists', name: 'Image exists' },
                        { id: 'upload_missing', name: 'Upload missing' },
                        { id: 'invalid_file_type', name: 'Invalid file type' },
                        { id: 'matches_regex', name: 'Matches regex' }
                      ]}
                      className="w-full"
                      buttonClassName="py-2 text-[10px] font-bold"
                      align="right"
                    />
                  </div>

                  {branch.rule.operator !== 'is_empty' && branch.rule.operator !== 'is_not_empty' && branch.rule.operator !== 'image_exists' && branch.rule.operator !== 'upload_missing' && branch.rule.operator !== 'invalid_file_type' && (
                    <input
                      type="text"
                      value={branch.rule.value}
                      onChange={(e) => updateBranchRule(branch.id, { value: e.target.value })}
                      placeholder="Value to compare..."
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-xl text-[10px] font-bold text-zinc-700 outline-none"
                    />
                  )}

                  <div className="pt-2 border-t border-zinc-200">
                    <label className="text-[11px] font-medium text-zinc-600 block mb-1">Route To</label>
                    <CustomDropdown
                      value={branch.targetNodeId || ''}
                      onChange={(val) => updateBranch(branch.id, { targetNodeId: val })}
                      options={[
                        { id: '', name: 'Select Target Node...' },
                        ...nodeOptions.map(opt => ({
                          id: opt.id,
                          name: opt.label
                        }))
                      ]}
                      className="w-full"
                      buttonClassName="py-2 text-[10px] font-bold"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-zinc-200/80">
              <label className="text-xs font-medium text-zinc-700 block mb-1.5">
                Default Route (If no match)
              </label>
              <CustomDropdown
                value={data.defaultTargetNodeId || ''}
                onChange={(val) => handleChange('defaultTargetNodeId', val)}
                options={[
                  { id: '', name: 'Select fallback node...' },
                  ...nodeOptions.map(opt => ({
                    id: opt.id,
                    name: opt.label
                  }))
                ]}
              />
            </div>
          </section>
        )}

        {data.type === 'integration' && (
          <section className="space-y-6 pt-4 border-t border-zinc-200/80">
            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                Select Integration Service
              </label>
              
              {loadingIntegrations ? (
                <div className="flex items-center gap-2 py-3 text-zinc-500 text-xs">
                  <Loader2 className="animate-spin" size={14} />
                  <span>Loading active integrations...</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <CustomDropdown
                    value={data.integrationConfig?.integrationType || ''}
                    onChange={(val) => {
                      const config = { ...(data.integrationConfig || {}), integrationType: val };
                      if (val !== 'freshchat') {
                        config.action = '';
                      } else {
                        config.action = 'handoff';
                      }
                      const activeLabel = integrationStatuses[val]?.label || 'Integration';
                      const actionLabel = config.action === 'handoff' ? 'Handoff' : '';
                      const nodeLabel = actionLabel ? `${activeLabel}: ${actionLabel}` : activeLabel;

                      updateNodeData(selectedNode.id, {
                        integrationConfig: config,
                        label: nodeLabel
                      });
                    }}
                    options={[
                      { id: '', name: '-- Choose Integration --' },
                      ...Object.entries(integrationStatuses).map(([key, info]) => ({
                        id: key,
                        name: `${info.label} (${info.active ? 'Active' : 'Inactive'})`
                      }))
                    ]}
                    renderOption={renderIntegrationOption}
                    renderSelected={renderIntegrationOption}
                  />
                </div>
              )}
            </div>

            {data.integrationConfig?.integrationType === 'freshchat' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                    Choose Action
                  </label>
                  <CustomDropdown
                    value={data.integrationConfig?.action || 'handoff'}
                    onChange={(val) => {
                      const config = { ...(data.integrationConfig || {}), action: val };
                      const activeLabel = integrationStatuses[config.integrationType]?.label || 'Integration';
                      const actionLabel = val === 'handoff' ? 'Handoff' : '';
                      const nodeLabel = actionLabel ? `${activeLabel}: ${actionLabel}` : activeLabel;

                      updateNodeData(selectedNode.id, {
                        integrationConfig: config,
                        label: nodeLabel
                      });
                    }}
                    options={[
                      { id: 'handoff', name: 'Human Handoff (Freshchat Inbox)' }
                    ]}
                    renderOption={(opt) => {
                      const logoUrl = INTEGRATION_LOGOS[data.integrationConfig?.integrationType];
                      return (
                        <div className="flex items-center gap-2">
                          {logoUrl && (
                            <img 
                              src={logoUrl} 
                              alt={opt.name} 
                              className="w-4 h-4 object-contain rounded-md shrink-0" 
                            />
                          )}
                          <span>{opt.name}</span>
                        </div>
                      );
                    }}
                    renderSelected={(opt) => {
                      const logoUrl = INTEGRATION_LOGOS[data.integrationConfig?.integrationType];
                      return (
                        <div className="flex items-center gap-2">
                          {logoUrl && (
                            <img 
                              src={logoUrl} 
                              alt={opt.name} 
                              className="w-4 h-4 object-contain rounded-md shrink-0" 
                            />
                          )}
                          <span>{opt.name}</span>
                        </div>
                      );
                    }}
                  />
                </div>
                
                <div className="p-4 rounded-xl bg-teal-50 border border-teal-100 space-y-2">
                  <div className="flex items-center gap-2 text-teal-700">
                    <Headphones size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Freshchat Handoff Active</span>
                  </div>
                  <p className="text-[10px] text-teal-600 leading-relaxed font-medium">
                    When executed, this node initiates a conversation inside your Freshchat Inbox.
                  </p>
                </div>
              </div>
            )}
          </section>
        )}

        {data.type === 'delay' && (
          <section className="space-y-4">
            <label className="text-xs font-medium text-zinc-700 block">
              Wait Time (ms)
            </label>
            <input
              type="number"
              value={data.delayMs || 1000}
              onChange={(e) => handleChange('delayMs', parseInt(e.target.value))}
              className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
            />
          </section>
        )}

        {data.type === 'attribute' && (
          <section className="space-y-4 pt-4 border-t border-zinc-200/80">
            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                Attribute Name
              </label>
              <CustomDropdown
                value={data.attributeName || ''}
                onChange={(val) => handleChange('attributeName', val)}
                options={[
                  { id: '', name: 'Select attribute...' },
                  { id: 'user.name',     name: 'User Name (user.name)' },
                  { id: 'user.email',    name: 'User Email (user.email)' },
                  { id: 'user.phone',    name: 'User Phone (user.phone)' },
                  { id: 'user.location', name: 'User Location (user.location)' },
                  { id: 'custom',        name: 'Custom Variable...' },
                ]}
              />
            </div>

            {data.attributeName === 'custom' && (
              <div>
                <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                  Custom Variable Name
                </label>
                <input
                  type="text"
                  value={data.variableName || ''}
                  onChange={(e) => handleChange('variableName', e.target.value)}
                  placeholder="e.g. user_age"
                  className="w-full px-4 py-2.5 bg-zinc-50 border border-zinc-200/80 rounded-xl text-sm font-bold text-zinc-900 focus:outline-none focus:ring-4 focus:ring-zinc-100 focus:border-zinc-900 transition-all"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-zinc-700 mb-1.5 block">
                Attribute Value
              </label>
              <input
                type="text"
                value={data.attributeValue !== undefined ? data.attributeValue : ''}
                onChange={(e) => handleChange('attributeValue', e.target.value)}
                placeholder="Enter value (supports {{variable}} templates)"
                className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:bg-white transition-colors"
              />
              <p className="mt-2 text-[10px] font-medium text-zinc-500 leading-relaxed">
                Set the value to store. You can use template syntax like <code>{`{{lastUserInput}}`}</code>.
              </p>
            </div>
          </section>
        )}
      </div>

    </div>
  );
};
